import type {
	Customer,
	EnrollmentExpiryReminderConfig,
} from "@pilatestime/shared-types";
import { NotificationType } from "@pilatestime/shared-types";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	Calendar,
	Copy,
	FileText,
	HelpCircle,
	Mail,
	Plus,
	Search,
	Settings,
	Trash2,
	UserPlus,
	Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchOrganizationCustomersPaginatedQueryOptions } from "@/api/customers";
import {
	fetchNotificationConfigQueryOptions,
	fetchNotificationExclusionsQueryOptions,
	useAddNotificationExclusion,
	useRemoveNotificationExclusion,
	useUpdateNotificationConfig,
} from "@/api/notifications";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { ToggleSwitch } from "@/components/ui/toggle-switch";
import { useApiParameters } from "@/hooks/useApiParameters";

// Available template variables for enrollment expiry notifications
const TEMPLATE_VARIABLES = [
	{ variable: "{{customerName}}", key: "customerName" },
	{ variable: "{{customerFirstName}}", key: "customerFirstName" },
	{ variable: "{{customerLastName}}", key: "customerLastName" },
	{ variable: "{{organizationName}}", key: "organizationName" },
	{ variable: "{{planName}}", key: "planName" },
	{ variable: "{{expiryDate}}", key: "expiryDate" },
	{ variable: "{{daysRemaining}}", key: "daysRemaining" },
] as const;

// Predefined email templates for enrollment expiry notifications
const EMAIL_TEMPLATES = {
	en_US: [
		{
			subject: "Your {{planName}} enrollment expires on {{expiryDate}}",
			body: `Dear {{customerFirstName}},

This is a friendly reminder that your {{planName}} enrollment at {{organizationName}} will expire on {{expiryDate}}.

To continue enjoying our services without interruption, please renew your enrollment before the expiry date.

If you have any questions or need assistance, please don't hesitate to contact us.

Best regards,
{{organizationName}}`,
		},
		{
			subject: "Reminder: {{daysRemaining}} days until your enrollment expires",
			body: `Hi {{customerFirstName}},

We wanted to remind you that your {{planName}} enrollment will expire in {{daysRemaining}} days ({{expiryDate}}).

Don't miss out! Renew now to maintain uninterrupted access to all your benefits.

Thank you for being a valued member of {{organizationName}}.

Warm regards,
The {{organizationName}} Team`,
		},
		{
			subject: "Action Required: Renew your {{planName}} enrollment",
			body: `Hello {{customerName}},

Your {{planName}} enrollment at {{organizationName}} is set to expire on {{expiryDate}}.

We'd love to continue having you as part of our community! Please visit us or contact our team to renew your enrollment.

Looking forward to seeing you soon!

Best,
{{organizationName}}`,
		},
	],
	pt_BR: [
		{
			subject: "Sua matrícula do {{planName}} expira em {{expiryDate}}",
			body: `Prezado(a) {{customerFirstName}},

Este é um lembrete amigável de que sua matrícula no plano {{planName}} em {{organizationName}} expirará em {{expiryDate}}.

Para continuar aproveitando nossos serviços sem interrupção, por favor renove sua matrícula antes da data de expiração.

Se você tiver alguma dúvida ou precisar de ajuda, não hesite em nos contatar.

Atenciosamente,
{{organizationName}}`,
		},
		{
			subject: "Lembrete: {{daysRemaining}} dias até sua matrícula expirar",
			body: `Olá {{customerFirstName}},

Queremos lembrá-lo(a) de que sua matrícula no plano {{planName}} expirará em {{daysRemaining}} dias ({{expiryDate}}).

Não perca! Renove agora para manter acesso ininterrupto a todos os seus benefícios.

Obrigado por ser um membro valioso de {{organizationName}}.

Abraços,
Equipe {{organizationName}}`,
		},
		{
			subject: "Ação Necessária: Renove sua matrícula do {{planName}}",
			body: `Olá {{customerName}},

Sua matrícula no plano {{planName}} em {{organizationName}} está programada para expirar em {{expiryDate}}.

Adoraríamos continuar tendo você como parte da nossa comunidade! Por favor, nos visite ou entre em contato com nossa equipe para renovar sua matrícula.

Esperamos vê-lo(a) em breve!

Atenciosamente,
{{organizationName}}`,
		},
	],
} as const;

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/events/enrollment-expiry-reminder/",
)({
	loader: async ({ context }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		return { userId, token };
	},
	component: EnrollmentExpiryReminderConfigPage,
});

function EnrollmentExpiryReminderConfigPage() {
	const { t, i18n } = useTranslation();
	const { userId, token } = Route.useLoaderData();
	const { orgId } = Route.useParams();
	// Fetch notification config
	const { data: config } = useSuspenseQuery(
		fetchNotificationConfigQueryOptions(
			userId,
			token,
			orgId,
			NotificationType.ENROLLMENT_EXPIRY_REMINDER,
		),
	);

	// Fetch exclusions (first page)
	const { data: exclusionsData } = useSuspenseQuery(
		fetchNotificationExclusionsQueryOptions(
			userId,
			token,
			orgId,
			NotificationType.ENROLLMENT_EXPIRY_REMINDER,
			1,
			10,
		),
	);

	// Parse config for enrollment expiry settings
	const configData = config.config as EnrollmentExpiryReminderConfig | null;
	const defaultNotifyOnExpiryDay = configData?.notifyOnExpiryDay ?? true;
	const defaultNotifyDaysBefore = configData?.notifyDaysBefore ?? true;
	const defaultDaysBeforeExpiry = configData?.daysBeforeExpiry ?? 5;

	// Local state for form - Master switch
	const [enabled, setEnabled] = useState(config.enabled);

	// Email channel state
	const [emailEnabled, setEmailEnabled] = useState(config.emailEnabled);
	const [messageSubject, setMessageSubject] = useState(
		config.messageSubject ?? "",
	);
	const [messageBody, setMessageBody] = useState(config.messageBody ?? "");

	// SMS channel state (UI hidden — values preserved from existing config)
	const [smsEnabled] = useState(config.smsEnabled);
	const [smsBody] = useState(config.smsBody ?? "");

	// WhatsApp channel state (UI hidden — values preserved from existing config)
	const [whatsappEnabled] = useState(config.whatsappEnabled);
	const [whatsappBody] = useState(config.whatsappBody ?? "");

	// Enrollment expiry specific settings
	const [notifyOnExpiryDay, setNotifyOnExpiryDay] = useState(
		defaultNotifyOnExpiryDay,
	);
	const [notifyDaysBefore, setNotifyDaysBefore] = useState(
		defaultNotifyDaysBefore,
	);
	const [daysBeforeExpiry, setDaysBeforeExpiry] = useState(
		defaultDaysBeforeExpiry,
	);

	// Template cycling state
	const [templateIndex, setTemplateIndex] = useState(0);

	// Add customer modal state
	const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
	const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
		null,
	);
	const [exclusionReason, setExclusionReason] = useState("");

	// Debounce search term
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
		}, 300);
		return () => clearTimeout(timer);
	}, [searchTerm]);

	// Reset modal state when closed
	useEffect(() => {
		if (!isAddCustomerOpen) {
			setSearchTerm("");
			setDebouncedSearchTerm("");
			setSelectedCustomer(null);
			setExclusionReason("");
		}
	}, [isAddCustomerOpen]);

	// Fetch customers for search
	const { data: customersData, isLoading: isLoadingCustomers } = useQuery({
		...fetchOrganizationCustomersPaginatedQueryOptions(
			userId,
			token,
			orgId,
			{ page: 1, limit: 15 },
			debouncedSearchTerm || undefined,
		),
		enabled: isAddCustomerOpen && debouncedSearchTerm.length >= 2,
	});

	const customers = customersData?.data || [];

	// Get currently excluded customer IDs
	const excludedCustomerIds = new Set(
		exclusionsData.data.map((e) => e.customerId),
	);

	// Mutations
	const updateConfigMutation = useUpdateNotificationConfig();
	const removeExclusionMutation = useRemoveNotificationExclusion();
	const addExclusionMutation = useAddNotificationExclusion();

	const handleSave = () => {
		updateConfigMutation.mutate({
			token,
			organizationId: orgId,
			notificationType: NotificationType.ENROLLMENT_EXPIRY_REMINDER,
			data: {
				enabled,
				// Email channel
				emailEnabled,
				messageSubject: messageSubject || null,
				messageBody: messageBody || null,
				// SMS channel
				smsEnabled,
				smsBody: smsBody || null,
				// WhatsApp channel
				whatsappEnabled,
				whatsappBody: whatsappBody || null,
				// Type-specific config
				config: { notifyOnExpiryDay, notifyDaysBefore, daysBeforeExpiry },
			},
		});
	};

	const handlePopulateFromTemplate = () => {
		const lang = i18n.language === "pt_BR" ? "pt_BR" : "en_US";
		const templates = EMAIL_TEMPLATES[lang];
		const template = templates[templateIndex % templates.length];

		setMessageSubject(template.subject);
		setMessageBody(template.body);

		// Cycle to next template for next click
		setTemplateIndex((prev) => (prev + 1) % templates.length);
	};

	const handleRemoveExclusion = (customerId: string) => {
		removeExclusionMutation.mutate({
			token,
			organizationId: orgId,
			notificationType: NotificationType.ENROLLMENT_EXPIRY_REMINDER,
			customerId,
		});
	};

	const handleAddExclusion = () => {
		if (!selectedCustomer) return;

		addExclusionMutation.mutate(
			{
				token,
				organizationId: orgId,
				notificationType: NotificationType.ENROLLMENT_EXPIRY_REMINDER,
				data: {
					customerId: selectedCustomer.id,
					reason: exclusionReason || undefined,
				},
			},
			{
				onSuccess: () => {
					setIsAddCustomerOpen(false);
				},
			},
		);
	};

	const hasChanges =
		enabled !== config.enabled ||
		// Email channel
		emailEnabled !== config.emailEnabled ||
		messageSubject !== (config.messageSubject ?? "") ||
		messageBody !== (config.messageBody ?? "") ||
		// SMS channel
		smsEnabled !== config.smsEnabled ||
		smsBody !== (config.smsBody ?? "") ||
		// WhatsApp channel
		whatsappEnabled !== config.whatsappEnabled ||
		whatsappBody !== (config.whatsappBody ?? "") ||
		// Type-specific config
		notifyOnExpiryDay !== defaultNotifyOnExpiryDay ||
		notifyDaysBefore !== defaultNotifyDaysBefore ||
		daysBeforeExpiry !== defaultDaysBeforeExpiry;

	return (
		<div className="flex flex-col h-full">
			<PageHeader
				title={t("notifications.enrollmentExpiry.title")}
				description={t("notifications.enrollmentExpiry.description")}
				showBackButton={true}
				backButtonText={t("common.back")}
				hideOnMobile={false}
			/>

			<div
				className="flex-1 p-3 md:p-4 bg-base-200 min-h-0 overflow-auto"
				data-testid="enrollment-expiry-config-container"
			>
				<div className="max-w-3xl mx-auto space-y-6">
					{/* Enable/Disable Card */}
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center space-x-2 text-base sm:text-lg">
								<Calendar className="h-5 w-5 text-primary shrink-0" />
								<span>{t("notifications.enrollmentExpiry.enableTitle")}</span>
							</CardTitle>
							<CardDescription>
								{t("notifications.enrollmentExpiry.enableDescription")}
							</CardDescription>
						</CardHeader>
						<CardContent>
							<div className="flex items-center justify-between">
								<Label htmlFor="enabled-switch" className="text-base">
									{t("notifications.enrollmentExpiry.enableLabel")}
								</Label>
								<ToggleSwitch
									id="enabled-switch"
									checked={enabled}
									onCheckedChange={setEnabled}
								/>
							</div>
						</CardContent>
					</Card>

					{/* Configuration Card */}
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center space-x-2 text-base sm:text-lg">
								<Settings className="h-5 w-5 text-primary shrink-0" />
								<span>{t("notifications.enrollmentExpiry.configTitle")}</span>
							</CardTitle>
							<CardDescription>
								{t("notifications.enrollmentExpiry.configDescription")}
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-6">
							{/* Notify on Expiry Day */}
							<div className="flex items-center justify-between">
								<div>
									<Label
										htmlFor="notify-on-expiry-day"
										className="text-base font-medium"
									>
										{t("notifications.enrollmentExpiry.notifyOnExpiryDayLabel")}
									</Label>
									<p className="text-sm text-base-content/60">
										{t(
											"notifications.enrollmentExpiry.notifyOnExpiryDayDescription",
										)}
									</p>
								</div>
								<ToggleSwitch
									id="notify-on-expiry-day"
									checked={notifyOnExpiryDay}
									onCheckedChange={setNotifyOnExpiryDay}
								/>
							</div>

							{/* Notify Days Before */}
							<div className="space-y-4">
								<div className="flex items-center justify-between">
									<div>
										<Label
											htmlFor="notify-days-before"
											className="text-base font-medium"
										>
											{t(
												"notifications.enrollmentExpiry.notifyDaysBeforeLabel",
											)}
										</Label>
										<p className="text-sm text-base-content/60">
											{t(
												"notifications.enrollmentExpiry.notifyDaysBeforeDescription",
											)}
										</p>
									</div>
									<ToggleSwitch
										id="notify-days-before"
										checked={notifyDaysBefore}
										onCheckedChange={setNotifyDaysBefore}
									/>
								</div>

								{/* Days Before Expiry Input - only visible when notifyDaysBefore is enabled */}
								{notifyDaysBefore && (
									<div className="ml-0 pl-4 border-l-2 border-primary/20">
										<Label htmlFor="days-before-expiry">
											{t(
												"notifications.enrollmentExpiry.daysBeforeExpiryLabel",
											)}
										</Label>
										<div className="flex items-center gap-2 mt-2">
											<Input
												id="days-before-expiry"
												type="number"
												min={1}
												max={30}
												value={daysBeforeExpiry}
												onChange={(e) =>
													setDaysBeforeExpiry(Number(e.target.value) || 5)
												}
												className="w-24"
											/>
											<span className="text-sm text-base-content/60">
												{t(
													"notifications.enrollmentExpiry.daysBeforeExpiryHint",
												)}
											</span>
										</div>
									</div>
								)}
							</div>
						</CardContent>
					</Card>

					{/* Notification Channels Card */}
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center space-x-2 text-base sm:text-lg">
								<Mail className="h-5 w-5 text-primary shrink-0" />
								<span>{t("notifications.channels.title")}</span>
							</CardTitle>
							<CardDescription>
								{t("notifications.channels.description")}
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-3">
							{/* Email Channel */}
							<div className="flex items-center justify-between gap-3 p-3 bg-base-200 rounded-lg">
								<div className="flex items-center gap-3 min-w-0 flex-1">
									<Mail className="h-5 w-5 text-primary shrink-0" />
									<div className="min-w-0">
										<p className="font-medium truncate">
											{t("notifications.channels.email")}
										</p>
										<p className="text-sm text-base-content/60 line-clamp-2">
											{t("notifications.channels.emailDescription")}
										</p>
									</div>
								</div>
								<ToggleSwitch
									id="email-enabled-switch"
									checked={emailEnabled}
									onCheckedChange={setEmailEnabled}
								/>
							</div>
						</CardContent>
					</Card>

					{/* Email Template Card */}
					{emailEnabled && (
						<Card>
							<CardHeader className="space-y-3">
								<div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
									<div className="min-w-0">
										<CardTitle className="flex items-center space-x-2 text-base sm:text-lg">
											<Mail className="h-5 w-5 text-primary shrink-0" />
											<span className="truncate">
												{t("notifications.enrollmentExpiry.messageTitle")}
											</span>
										</CardTitle>
										<CardDescription className="mt-1.5">
											{t("notifications.enrollmentExpiry.messageDescription")}
										</CardDescription>
									</div>
									<div className="flex items-center gap-2 shrink-0">
										<Button
											variant="outline"
											size="sm"
											className="flex items-center gap-1 text-xs sm:text-sm"
											onClick={handlePopulateFromTemplate}
										>
											<FileText className="h-4 w-4" />
											<span className="hidden xs:inline">
												{t("notifications.enrollmentExpiry.useTemplateButton")}
											</span>
											<span className="xs:hidden">{t("common.template")}</span>
										</Button>
										<Popover>
											<PopoverTrigger asChild>
												<Button
													variant="outline"
													size="sm"
													className="flex items-center gap-1 text-xs sm:text-sm"
												>
													<HelpCircle className="h-4 w-4" />
													<span className="hidden xs:inline">
														{t(
															"notifications.enrollmentExpiry.variablesButton",
														)}
													</span>
													<span className="xs:hidden">
														{t("common.variables")}
													</span>
												</Button>
											</PopoverTrigger>
											<PopoverContent
												align="end"
												className="w-[calc(100vw-2rem)] sm:w-80"
											>
												<div className="space-y-3">
													<h4 className="font-medium text-sm sm:text-base">
														{t("notifications.enrollmentExpiry.variablesTitle")}
													</h4>
													<p className="text-xs sm:text-sm text-base-content/70">
														{t(
															"notifications.enrollmentExpiry.variablesDescription",
														)}
													</p>
													<div className="space-y-2">
														{TEMPLATE_VARIABLES.map((item) => (
															<div
																key={item.variable}
																className="flex items-center justify-between gap-2 p-2 bg-base-200 rounded-md"
															>
																<div className="min-w-0 flex-1">
																	<code className="text-xs sm:text-sm font-mono text-primary break-words">
																		{item.variable}
																	</code>
																	<p className="text-xs text-base-content/60 mt-0.5 line-clamp-2">
																		{t(
																			`notifications.enrollmentExpiry.variables.${item.key}`,
																		)}
																	</p>
																</div>
																<Button
																	variant="ghost"
																	size="sm"
																	className="h-7 px-2 shrink-0"
																	onClick={() => {
																		navigator.clipboard.writeText(
																			item.variable,
																		);
																	}}
																>
																	<Copy className="h-3.5 w-3.5" />
																</Button>
															</div>
														))}
													</div>
												</div>
											</PopoverContent>
										</Popover>
									</div>
								</div>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="space-y-2">
									<Label htmlFor="message-subject">
										{t("notifications.enrollmentExpiry.subjectLabel")}
									</Label>
									<input
										id="message-subject"
										type="text"
										className="input input-bordered w-full"
										placeholder={t(
											"notifications.enrollmentExpiry.subjectPlaceholder",
										)}
										value={messageSubject}
										onChange={(e) => setMessageSubject(e.target.value)}
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="message-body">
										{t("notifications.enrollmentExpiry.bodyLabel")}
									</Label>
									<Textarea
										id="message-body"
										className="min-h-32"
										placeholder={t(
											"notifications.enrollmentExpiry.bodyPlaceholder",
										)}
										value={messageBody}
										onChange={(e) => setMessageBody(e.target.value)}
									/>
									<p className="text-xs text-base-content/60">
										{t("notifications.enrollmentExpiry.templateHint")}
									</p>
								</div>
							</CardContent>
						</Card>
					)}

					{/* Excluded Customers Card */}
					<Card>
						<CardHeader className="space-y-3">
							<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
								<CardTitle className="flex items-center space-x-2 text-base sm:text-lg">
									<Users className="h-5 w-5 text-primary shrink-0" />
									<span className="truncate">
										{t("notifications.enrollmentExpiry.exclusionsTitle")}
									</span>
								</CardTitle>
								<Button
									variant="outline"
									size="sm"
									onClick={() => setIsAddCustomerOpen(true)}
									className="flex items-center gap-1 w-full sm:w-auto justify-center"
								>
									<UserPlus className="h-4 w-4" />
									{t("notifications.enrollmentExpiry.addExclusion")}
								</Button>
							</div>
							<CardDescription>
								{t("notifications.enrollmentExpiry.exclusionsDescription")}
							</CardDescription>
						</CardHeader>
						<CardContent>
							{exclusionsData.data.length === 0 ? (
								<p className="text-base-content/60 text-center py-4">
									{t("notifications.enrollmentExpiry.noExclusions")}
								</p>
							) : (
								<div className="space-y-2">
									{exclusionsData.data.map((exclusion) => (
										<div
											key={exclusion.id}
											className="flex items-start sm:items-center justify-between gap-3 p-3 bg-base-200 rounded-lg"
										>
											<div className="min-w-0 flex-1">
												<p className="font-medium truncate">
													{exclusion.customer?.firstName}{" "}
													{exclusion.customer?.lastName}
												</p>
												{exclusion.customer?.idType &&
													exclusion.customer?.idNumber && (
														<p className="text-sm text-base-content/60 truncate">
															{exclusion.customer.idType}:{" "}
															{exclusion.customer.idNumber}
														</p>
													)}
												{exclusion.customer?.email && (
													<p className="text-sm text-base-content/60 truncate">
														{exclusion.customer.email}
													</p>
												)}
												{exclusion.reason && (
													<p className="text-xs text-base-content/50 mt-1 line-clamp-2">
														{t("notifications.enrollmentExpiry.reason")}:{" "}
														{exclusion.reason}
													</p>
												)}
											</div>
											<Button
												variant="ghost"
												size="sm"
												onClick={() =>
													handleRemoveExclusion(exclusion.customerId)
												}
												disabled={removeExclusionMutation.isPending}
												className="text-error hover:text-error hover:bg-error/10 shrink-0"
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										</div>
									))}
									{exclusionsData.pagination.totalPages > 1 && (
										<p className="text-sm text-base-content/60 text-center pt-2">
											{t("notifications.enrollmentExpiry.moreExclusions", {
												total: exclusionsData.pagination.total,
											})}
										</p>
									)}
								</div>
							)}
						</CardContent>
					</Card>

					{/* Save Button */}
					<div className="flex justify-center sm:justify-end pb-6">
						<Button
							onClick={handleSave}
							disabled={!hasChanges || updateConfigMutation.isPending}
							className="w-full sm:w-auto min-w-32 text-primary-content"
						>
							{updateConfigMutation.isPending
								? t("common.saving")
								: t("common.save")}
						</Button>
					</div>
				</div>
			</div>

			{/* Add Customer Exclusion Dialog */}
			<Dialog open={isAddCustomerOpen} onOpenChange={setIsAddCustomerOpen}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>
							{t("notifications.enrollmentExpiry.addExclusionTitle")}
						</DialogTitle>
						<DialogDescription>
							{t("notifications.enrollmentExpiry.addExclusionDescription")}
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-4">
						{/* Customer Search */}
						{!selectedCustomer ? (
							<div className="space-y-3">
								<div className="relative">
									<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-base-content/50" />
									<Input
										placeholder={t(
											"notifications.enrollmentExpiry.searchCustomerPlaceholder",
										)}
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
										className="pl-9"
									/>
								</div>

								{debouncedSearchTerm.length > 0 &&
									debouncedSearchTerm.length < 2 && (
										<p className="text-sm text-base-content/60 text-center">
											{t("notifications.enrollmentExpiry.searchMinChars")}
										</p>
									)}

								{isLoadingCustomers && (
									<div className="flex justify-center py-4">
										<span className="loading loading-spinner loading-sm" />
									</div>
								)}

								{!isLoadingCustomers &&
									debouncedSearchTerm.length >= 2 &&
									customers.length === 0 && (
										<p className="text-sm text-base-content/60 text-center py-4">
											{t("notifications.enrollmentExpiry.noCustomersFound")}
										</p>
									)}

								{!isLoadingCustomers && customers.length > 0 && (
									<div className="max-h-48 overflow-y-auto space-y-1">
										{customers.map((customer) => {
											const isAlreadyExcluded = excludedCustomerIds.has(
												customer.id,
											);
											return (
												<button
													key={customer.id}
													type="button"
													disabled={isAlreadyExcluded}
													onClick={() => setSelectedCustomer(customer)}
													className={`w-full text-left p-2 rounded-lg transition-colors ${
														isAlreadyExcluded
															? "bg-base-200 opacity-50 cursor-not-allowed"
															: "hover:bg-base-200 cursor-pointer"
													}`}
												>
													<p className="font-medium">
														{customer.firstName} {customer.lastName}
													</p>
													{customer.idType && customer.idNumber && (
														<p className="text-sm text-base-content/60">
															{customer.idType}: {customer.idNumber}
														</p>
													)}
													{customer.email && (
														<p className="text-sm text-base-content/60">
															{customer.email}
														</p>
													)}
													{isAlreadyExcluded && (
														<p className="text-xs text-warning">
															{t(
																"notifications.enrollmentExpiry.alreadyExcluded",
															)}
														</p>
													)}
												</button>
											);
										})}
									</div>
								)}
							</div>
						) : (
							<div className="space-y-3">
								{/* Selected Customer */}
								<div className="flex items-center justify-between p-3 bg-base-200 rounded-lg">
									<div>
										<p className="font-medium">
											{selectedCustomer.firstName} {selectedCustomer.lastName}
										</p>
										{selectedCustomer.idType && selectedCustomer.idNumber && (
											<p className="text-sm text-base-content/60">
												{selectedCustomer.idType}: {selectedCustomer.idNumber}
											</p>
										)}
										{selectedCustomer.email && (
											<p className="text-sm text-base-content/60">
												{selectedCustomer.email}
											</p>
										)}
									</div>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => setSelectedCustomer(null)}
									>
										{t("common.change")}
									</Button>
								</div>

								{/* Reason Input */}
								<div className="space-y-2">
									<Label htmlFor="exclusion-reason">
										{t("notifications.enrollmentExpiry.reasonLabel")}
									</Label>
									<Input
										id="exclusion-reason"
										placeholder={t(
											"notifications.enrollmentExpiry.reasonPlaceholder",
										)}
										value={exclusionReason}
										onChange={(e) => setExclusionReason(e.target.value)}
									/>
									<p className="text-xs text-base-content/60">
										{t("notifications.enrollmentExpiry.reasonHint")}
									</p>
								</div>
							</div>
						)}
					</div>

					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setIsAddCustomerOpen(false)}
						>
							{t("common.cancel")}
						</Button>
						<Button
							onClick={handleAddExclusion}
							disabled={!selectedCustomer || addExclusionMutation.isPending}
							className="text-primary-content"
						>
							{addExclusionMutation.isPending ? (
								<span className="loading loading-spinner loading-xs" />
							) : (
								<>
									<Plus className="h-4 w-4 mr-1" />
									{t("notifications.enrollmentExpiry.addExclusionButton")}
								</>
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
